import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PasswordEyeDirective } from '@catch/directives';
import { ButtonComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { passwordPattern } from '../../../../../../assets/regex';
import { confirmField } from '../../../../../validators/confirmField';
import { PlatformAuthService } from '../../../../services/platform-auth.service';

@Component({
  selector: 'app-platform-set-password-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslocoPipe, PasswordEyeDirective, ButtonComponent],
  templateUrl: './platform-set-password-form.component.html',
  styleUrl: './platform-set-password-form.component.scss',
})
export class PlatformSetPasswordFormComponent {
  private readonly toastsService = inject(ToastsService);
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(PlatformAuthService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly nonce = input<string | null>(null);
  readonly isPasswordReset = input(false);
  readonly completed = output<{ registerSuccess: boolean }>();

  readonly setPasswordForm: FormGroup = this.fb.group(
    {
      password: this.fb.control(null, [Validators.required, Validators.pattern(passwordPattern)]),
      passwordConfirm: this.fb.control(null, [Validators.required]),
    },
    {
      validators: [confirmField('password', 'passwordConfirm', 'passwordMismatch')],
    }
  );
  setPasswordFormSubmitAttempt = false;
  setPasswordFormSubmitting = false;

  /**
   * Submits the set-password form with the mailed nonce.
   */
  onSetPasswordFormSubmit(): void {
    this.setPasswordFormSubmitAttempt = true;
    const nonce = this.nonce();
    if (!nonce) {
      this.toastsService.warning('jobboard.platform.auth.set-password.error');
      return;
    }
    if (this.setPasswordForm.valid) {
      this.setPasswordFormSubmitting = true;
      this.auth.setPasswordWithNonce(nonce, this.password.value).subscribe({
        next: () => {
          this.toastsService.success('jobboard.platform.auth.set-password.success-toast');
          this.completed.emit({ registerSuccess: !this.isPasswordReset() });
          this.setPasswordFormSubmitting = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.toastsService.warning('jobboard.platform.auth.set-password.error');
          this.setPasswordFormSubmitting = false;
          this.cdr.markForCheck();
        },
      });
    }
  }

  get password() {
    return this.setPasswordForm.get('password')!;
  }

  get passwordConfirm() {
    return this.setPasswordForm.get('passwordConfirm')!;
  }
}
